"use client";
import { createContext, useContext, useState, useEffect } from 'react';

const StoreContext = createContext();

export function StoreProvider({ children }) {
  const [selectedStore, setSelectedStore] = useState(null);
  const [stores, setStores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load stores and preference on mount
  useEffect(() => {
    loadStoresAndPreference();
  }, []);

  const loadStoresAndPreference = async () => {
    try {
      const res = await fetch('/api/user-stores');
      
      // Check if response is JSON or HTML (redirected to signin)
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Response is HTML (probably redirected to signin page)
        console.warn('[StoreContext] API returned non-JSON response, user likely not authenticated');
        setStores([]);
        setIsLoading(false);
        return;
      }

      // Check if request was successful
      if (!res.ok) {
        if (res.status === 401) {
          // User not authenticated - this is expected on signin page
          console.log('[StoreContext] User not authenticated, skipping store load');
          setStores([]);
          setIsLoading(false);
          return;
        }
        throw new Error(`API error: ${res.status}`);
      }

      const data = await res.json();

      if (data.success) {
        setStores(data.stores);
        
        // Use API preference first, then fallback to cookie/localStorage
        if (data.lastSelected) {
          setSelectedStore(data.lastSelected);
          // Sync with cookie
          document.cookie = `selectedStore=${data.lastSelected}; path=/`;
        } else {
          // Check cookie as fallback
          const cookieStore = getCookieValue('selectedStore');
          if (cookieStore) {
            setSelectedStore(cookieStore);
          }
        }
      } else {
        setStores([]);
      }
    } catch (error) {
      // Only log if it's not a JSON parse error (which we handle above)
      if (!(error instanceof SyntaxError && error.message.includes('JSON'))) {
        console.error('Error loading stores:', error);
      }
      setStores([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getCookieValue = (name) => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  };

  // Save preference to API and cookie
  const selectStore = async (shop) => {
    try {
      // Update API
      const res = await fetch('/api/user-stores/select', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop })
      });

      if (res.ok) {
        setSelectedStore(shop);
        
        // Set cookie for immediate use
        if (typeof window !== 'undefined') {
          document.cookie = `selectedStore=${shop}; path=/`;
        }
        
        return true;
      }
    } catch (error) {
      console.error('Error selecting store:', error);
      return false;
    }
  };

  return (
    <StoreContext.Provider value={{
      selectedStore,
      stores,
      setStores,
      selectStore,
      isLoading,
      refreshStores: loadStoresAndPreference
    }}>
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within StoreProvider');
  }
  return context;
};